// 체크한 항목만 보이게 하는 드롭다운 (과제/업무 관리의 유형·컬럼 선택에 쓴다)
// 고른 값은 브라우저에 저장해 다음에 들어와도 유지한다.

import { useCallback, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import Checkbox from '@/components/ui/Checkbox'
import { designTokens } from '@/utils/design-tokens'

export interface CheckOption<T extends string> {
  value: T
  label: string
}

/**
 * 보일 항목 목록을 브라우저에 저장한다.
 * 숨긴 항목을 저장하므로 나중에 새 항목이 생기면 기본으로 보인다.
 */
export const usePersistedVisible = <T extends string>(storageKey: string, allValues: T[]) => {
  const [hidden, setHidden] = useState<T[]>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]')
      return Array.isArray(parsed) ? allValues.filter(value => parsed.includes(value)) : []
    } catch {
      return []
    }
  })

  const setVisible = useCallback(
    (visible: T[]) => {
      const nextHidden = allValues.filter(value => !visible.includes(value))
      setHidden(nextHidden)
      try {
        localStorage.setItem(storageKey, JSON.stringify(nextHidden))
      } catch {
        // 저장이 막힌 브라우저에서도 화면 필터는 동작한다
      }
    },
    [allValues, storageKey]
  )

  const visible = useMemo(() => allValues.filter(value => !hidden.includes(value)), [allValues, hidden])
  return { visible, setVisible }
}

interface CheckDropdownProps<T extends string> {
  options: CheckOption<T>[]
  visible: T[]
  onChange: (visible: T[]) => void
  /** 모두 골랐을 때 버튼 글자 (예: '[전체] 유형 선택') */
  allLabel: string
  /** 일부만 골랐을 때 버튼 글자 */
  partialLabel: (count: number) => string
  /** 최소로 남겨야 하는 개수. 1 이면 마지막 하나는 끌 수 없다 */
  minSelected?: number
}

export default function CheckDropdown<T extends string>({
  options,
  visible,
  onChange,
  allLabel,
  partialLabel,
  minSelected = 0,
}: CheckDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const allValues = options.map(option => option.value)
  const isAll = visible.length === options.length

  const buttonLabel = isAll
    ? allLabel
    : visible.length === 1
      ? options.find(option => option.value === visible[0])?.label ?? partialLabel(1)
      : partialLabel(visible.length)

  const toggle = (value: T, checked: boolean) => {
    const next = checked
      ? allValues.filter(v => v === value || visible.includes(v))
      : visible.filter(v => v !== value)
    if (next.length >= minSelected) onChange(next)
  }

  const textStyle = {
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: designTokens.typography.fontSize['body-medium'][0],
    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
    color: designTokens.colors.gray[6],
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          ...textStyle,
          backgroundColor: designTokens.colors.white,
          borderColor: isAll ? designTokens.colors.gray[1] : designTokens.colors.primary.DEFAULT,
        }}
      >
        <span className="truncate">{buttonLabel}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isAll && (
            <span
              onClick={e => {
                e.stopPropagation()
                onChange(allValues)
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              style={{ fontSize: '14px' }}
              title="전체 보기"
            >
              ✕
            </span>
          )}
          <ChevronDown className="w-5 h-5" style={{ color: designTokens.colors.gray[3] }} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg py-1 max-h-80 overflow-y-auto"
            style={{ borderColor: designTokens.colors.gray[1] }}
          >
            <div className="px-3 py-2 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
              <Checkbox checked={isAll} onChange={() => onChange(allValues)} label="전체" size="small" />
            </div>
            {options.map(({ value, label }) => {
              const checked = visible.includes(value)
              return (
                <div key={value} className="px-3 py-2 hover:bg-gray-50">
                  <Checkbox
                    checked={checked}
                    onChange={next => toggle(value, next)}
                    disabled={checked && visible.length <= minSelected}
                    label={label}
                    size="small"
                  />
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
